import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../components/queryBuilder/queryBuilderUtils";
import type { ConditionFieldListOptions } from "../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import {
  getBooleanOperators,
  getNumericOperators,
  getStringOperators,
  getTimestampOperators,
  getUnknownDatatypeOperators,
  type FieldDefinition,
  type QueryBuilderConfig,
  type QueryBuilderValue,
} from "../../../../data-model/queryBuilder";
import type {
  CmmInputUniqueValueItem,
  CmmInputUniqueValuesColumn,
  CmmInputUniqueValuesDoc,
} from "../validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

/** Firestore document shape at `/configurations/pharmacy_order_updates/rules/calling`. `set_random` stores value as string[]; other types use a single string. */
export interface CallingRuleAction {
  type: string;
  field: string;
  value: string | string[];
}

export type CallingRuleTrigger =
  | { type: "cron"; schedule: string }
  | { type: "api" }
  | { type: "order_start" }
  | { type: "before_stp" }
  | { type: string; schedule?: string };

export interface CallingFirestoreRule {
  label: string;
  subtext?: string;
  active?: boolean;
  actions?: CallingRuleAction[];
  /** @deprecated Legacy per-rule triggers; use document-level `triggers` instead. */
  triggers?: CallingRuleTrigger[];
  /** Newer docs use `rule`, older ones use `when`. Preserve whichever is present. */
  rule?: QueryBuilderValue;
  when?: QueryBuilderValue;
}

export interface CallingRuleDoc {
  id: string;
  triggers?: CallingRuleTrigger[];
  rules: CallingFirestoreRule[];
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

/** UI-side rule state. Adds a stable `id` for React keys and tracks which condition key was used in Firestore. */
export interface CallingRuleState {
  id: string;
  label: string;
  subtext?: string;
  active: boolean;
  actions: CallingRuleAction[];
  condition: QueryBuilderValue;
  /** "rule" or "when" — original Firestore key, preserved on write. */
  conditionKey: "rule" | "when";
}

export const DEFAULT_CALLING_DOC_ID = "calling";

export const DEFAULT_CALLING_DOC_TRIGGERS: CallingRuleTrigger[] = [
  { type: "cron", schedule: "0 12 * * *" },
  { type: "api" },
];

const SUPPORTED_TRIGGER_TYPES = new Set([
  "api",
  "cron",
  "order_start",
  "before_stp",
  "before_second_stp",
  "after_first_stp",
  "after_second_stp",
]);

/**
 * Normalizes a trigger type and rejects values outside the supported vocabulary.
 *
 * @param raw - Trigger type read from Firestore
 * @returns Canonical trigger type, or an empty string when unsupported
 */
export function normalizeTriggerType(raw: string): string {
  const key = raw.trim().toLowerCase();
  return SUPPORTED_TRIGGER_TYPES.has(key) ? key : "";
}

/**
 * Normalizes a Firestore trigger entry (object or string) into a UI trigger.
 *
 * @param raw - Document-level or per-rule trigger value from Firestore
 * @returns Normalized trigger, or null when the entry cannot be parsed
 */
export function normalizeTriggerFromFirestore(
  raw: unknown,
): CallingRuleTrigger | null {
  if (typeof raw === "string") {
    const type = normalizeTriggerType(raw);
    if (!type) return null;
    return type === "cron"
      ? { type: "cron", schedule: "0 12 * * *" }
      : { type };
  }
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const type = normalizeTriggerType(String(record.type ?? ""));
  if (!type) return null;
  if (type === "cron") {
    const schedule =
      String(record.schedule ?? record.cron ?? record.crontab ?? "").trim() ||
      "0 12 * * *";
    return { type: "cron", schedule };
  }
  return { type };
}

/** Normalizes a triggers array from Firestore, dropping invalid entries. */
export function normalizeTriggersList(raw: unknown): CallingRuleTrigger[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(normalizeTriggerFromFirestore)
    .filter((trigger): trigger is CallingRuleTrigger => trigger != null);
}

/** Collects unique legacy per-rule triggers from every rule in a document. */
export function collectLegacyRuleTriggers(
  doc: CallingRuleDoc,
): CallingRuleTrigger[] {
  const triggers: CallingRuleTrigger[] = [];
  const seen = new Set<string>();
  for (const rule of doc.rules ?? []) {
    for (const raw of rule.triggers ?? []) {
      const normalized = normalizeTriggerFromFirestore(raw);
      if (!normalized) continue;
      const dedupeKey =
        normalized.type === "cron"
          ? `cron:${(normalized as { schedule?: string }).schedule ?? ""}`
          : normalized.type;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      triggers.push(normalized);
    }
  }
  return triggers;
}

/**
 * Resolves document-level triggers with legacy per-rule fallback.
 *
 * @param doc - Firestore rule-group document
 * @param defaultTriggers - Defaults when no triggers are stored anywhere
 * @returns Triggers for the configuration UI
 */
export function resolveDocTriggersWithDefault(
  doc: CallingRuleDoc,
  defaultTriggers: CallingRuleTrigger[],
): CallingRuleTrigger[] {
  const fromDoc = normalizeTriggersList(doc.triggers);
  if (fromDoc.length > 0) {
    return fromDoc;
  }
  const fromRules = collectLegacyRuleTriggers(doc);
  if (fromRules.length > 0) {
    return fromRules;
  }
  return defaultTriggers.map((t) => ({ ...t }));
}

/** Normalize a trigger for Firestore (type only, plus schedule for cron). */
export function sanitizeTriggerForFirestore(
  trigger: CallingRuleTrigger,
): CallingRuleTrigger {
  const type = normalizeTriggerType(trigger.type);
  if (type === "cron") {
    const schedule =
      (trigger as { schedule?: string }).schedule?.trim() || "0 12 * * *";
    return { type: "cron", schedule };
  }
  return { type };
}

/** Resolve document-level triggers, falling back to legacy per-rule triggers on load. */
export function resolveDocTriggers(doc: CallingRuleDoc): CallingRuleTrigger[] {
  return resolveDocTriggersWithDefault(doc, DEFAULT_CALLING_DOC_TRIGGERS);
}

/** Ensure values are string | string[] AND inject ids on groups/conditions — the Firestore doc may store them without ids, but QueryBuilder uses `id` for its change handlers and React keys. */
function normalizeConditionForQueryBuilder(
  condition: QueryBuilderValue,
): QueryBuilderValue {
  return {
    ...condition,
    id: condition.id ?? uuidv4(),
    conjunction: condition.conjunction ?? "AND",
    children: (condition.children ?? []).map((child) =>
      child.type === "condition"
        ? {
            ...child,
            id: child.id ?? uuidv4(),
            value:
              typeof child.value === "number"
                ? String(child.value)
                : Array.isArray(child.value)
                  ? child.value.map((v) =>
                      typeof v === "number" ? String(v) : v,
                    )
                  : (child.value ?? ""),
          }
        : normalizeConditionForQueryBuilder(child),
    ),
  } as QueryBuilderValue;
}

export function mapCallingDocToRuleStates(
  doc: CallingRuleDoc,
): CallingRuleState[] {
  return (doc.rules ?? []).map((rule, index) => {
    const conditionKey: "rule" | "when" =
      rule.rule != null ? "rule" : rule.when != null ? "when" : "rule";
    const condition = rule.rule ?? rule.when ?? createEmptyGroup();
    return {
      id: `calling-rule-${index}-${uuidv4()}`,
      label: rule.label ?? `Rule ${index + 1}`,
      subtext: rule.subtext,
      active: rule.active ?? true,
      actions: (rule.actions ?? []).map((a) => ({ ...a })),
      condition: normalizeConditionForQueryBuilder(condition),
      conditionKey,
    };
  });
}

export function mapRuleStatesToFirestorePayload(
  rules: CallingRuleState[],
  triggers: CallingRuleTrigger[],
  existingDoc: CallingRuleDoc | null,
  updatedBy?: string | null,
): Pick<
  CallingRuleDoc,
  | "id"
  | "triggers"
  | "rules"
  | "created_at"
  | "created_by"
  | "updated_at"
  | "updated_by"
> {
  const nowIso = new Date().toISOString();
  return {
    id: existingDoc?.id ?? DEFAULT_CALLING_DOC_ID,
    triggers: triggers.map(sanitizeTriggerForFirestore),
    rules: rules.map((r) => {
      const conditionField =
        r.conditionKey === "when"
          ? { when: r.condition }
          : { rule: r.condition };
      return {
        label: r.label,
        subtext: r.subtext ?? "",
        active: r.active,
        actions: r.actions.map((a) => ({ ...a })),
        ...conditionField,
      };
    }),
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: nowIso,
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}

export function createEmptyCallingRule(index: number): CallingRuleState {
  return {
    id: `calling-rule-${uuidv4()}`,
    label: `Rule ${index + 1}`,
    subtext: "",
    active: true,
    actions: [{ type: "set", field: "", value: "" }],
    condition: createEmptyGroup(),
    conditionKey: "rule",
  };
}

export const getDefaultCallingRules = (): CallingRuleState[] => [
  createEmptyCallingRule(0),
];

/** Form data shape consumed by the AddNewCallingRuleModal. */
export interface CallingRuleFormData {
  label: string;
  subtext: string;
  active: boolean;
  actions: CallingRuleAction[];
}

function topValueToOption(
  t: CmmInputUniqueValueItem | string | number | boolean,
): { value: string; label: string } {
  const isItemShape = t != null && typeof t === "object" && "value" in t;
  const value = isItemShape ? (t as CmmInputUniqueValueItem).value : t;
  const count = isItemShape ? (t as CmmInputUniqueValueItem).count : undefined;
  const valueStr = String(value ?? "").slice(0, 80);
  const suffix = String(value ?? "").length > 80 ? "…" : "";
  const countPart = count !== undefined ? ` (${count})` : "";
  return {
    value: String(value ?? ""),
    label: `${valueStr}${suffix}${countPart}`,
  };
}

function buildFieldFromCmmColumn(
  col: CmmInputUniqueValuesColumn,
): FieldDefinition {
  const valueOptionsFromTopValues =
    col.top_values?.length > 0
      ? col.top_values.slice(0, 100).map(topValueToOption)
      : undefined;

  const dt = (col.data_type ?? "").toUpperCase();
  let operators: ReturnType<typeof getStringOperators>;

  if (dt === "STRING") {
    operators = getStringOperators(valueOptionsFromTopValues ?? undefined);
  } else if (
    dt === "INTEGER" ||
    dt === "FLOAT" ||
    dt === "FLOAT64" ||
    dt === "FLOAT32" ||
    dt === "INT64" ||
    dt === "INT32"
  ) {
    operators = getNumericOperators();
  } else if (dt === "BOOLEAN") {
    operators = getBooleanOperators();
  } else if (dt === "TIMESTAMP" || dt === "DATE" || dt === "DATETIME") {
    operators = getTimestampOperators(valueOptionsFromTopValues ?? undefined);
  } else {
    operators = getUnknownDatatypeOperators(
      valueOptionsFromTopValues ?? undefined,
    );
  }

  const rawLabel = col.display_name || col.column_name.replace(/_/g, " ");
  return {
    value: col.column_name,
    label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
    operators,
    dataType: col.data_type,
    valueOptions: valueOptionsFromTopValues,
  };
}

/**
 * Builds QueryBuilderConfig for the calling configuration page from the CMM input
 * unique values Firestore document.
 *
 * @param doc - CMM input unique values document from Firestore
 * @param options - When `filterByShowOnConditionList` is false, all columns are included
 * @returns Query builder config with field definitions derived from document columns
 */
export function buildCallingConditionConfigFromCmmColumns(
  doc: CmmInputUniqueValuesDoc | null,
  options?: ConditionFieldListOptions,
): QueryBuilderConfig {
  if (!doc?.columns?.length) {
    return { fields: [] };
  }
  const filterByShowOnConditionList =
    options?.filterByShowOnConditionList !== false;
  const columns = filterByShowOnConditionList
    ? doc.columns.filter((col) => col.show_on_condition_list)
    : doc.columns;
  return {
    fields: columns.map(buildFieldFromCmmColumn),
  };
}

/** Calling configuration condition dropdowns include columns hidden from other pages. */
export const CALLING_CONDITION_FIELD_OPTIONS: ConditionFieldListOptions = {
  filterByShowOnConditionList: false,
};

export const CALLING_CONDITION_TITLE = "Conditions (All Must Match)";

export const formatTriggerLabel = (trigger: CallingRuleTrigger): string => {
  if (trigger.type === "cron") {
    const schedule = (trigger as { schedule?: string }).schedule ?? "";
    return schedule ? `cron · ${schedule}` : "cron";
  }
  return trigger.type;
};
